/**
 * AddBuildingLocatePanel — Step 1 of the Add Building flow. One search field
 * plus a live debounced dropdown of up to 5 ranked candidates, replacing the
 * tabbed Address / BIN / CSV input. Clicking a row bypasses the geocoder
 * ranker that mis-resolved "112 MADISON AVENUE, New York, 10016" to a
 * different building (bug #37).
 *
 * Pixel source: /tmp/design-handoff/carbon-clear/project/add-building-flow.jsx
 * — `LocatePanel` 862-921, freeze-frame `A1_Entry` 219-292. 60/30/10 palette
 * from src/index.css (`teal` / `ink-*` / `paper-panel`); Newsreader headline
 * (28px / 400 / -0.6 tracking), JetBrains Mono for the eyebrow and the
 * per-row "BIN xxx · BBL xxx" caption.
 *
 * Contract: `onPick(c)` advances the parent to Step 2 with that row's
 * BIN/BBL/address; `onOpenCsv()` fires from the footer link; `existingBins`
 * disables rows already in the company's portfolio with an "already added"
 * hint.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { locate, type LocateCandidate } from "@/api/tracking";
import MonoLabel from "@/components/design/MonoLabel";

// 250ms: responsive on a held key, long enough to skip intermediate states
// while the user is typing fast.
const DEBOUNCE_MS = 250;
const MIN_QUERY = 3;

interface Props {
  /** BINs already in the company's portfolio; matching rows render disabled.
   *  Omitted, no row is dimmed. */
  existingBins?: Set<string>;
  /** Fires on a candidate click, advancing into Step 2. */
  onPick: (candidate: LocateCandidate) => void;
  /** Fires from the footer "Bulk upload (CSV)" affordance. */
  onOpenCsv?: () => void;
}

export const AddBuildingLocatePanel: React.FC<Props> = ({
  existingBins,
  onPick,
  onOpenCsv,
}) => {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<LocateCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The most recent query typed, so a slow earlier response cannot clobber a
  // fresher result when both land.
  const latestQueryRef = useRef("");

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setCandidates([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    latestQueryRef.current = trimmed;

    const timer = setTimeout(async () => {
      try {
        const res = await locate(trimmed);
        // Race guard — drop the response if the user has typed further.
        if (latestQueryRef.current !== trimmed) return;
        setCandidates(res.candidates ?? []);
        setError(res.error ?? null);
      } catch (e) {
        if (latestQueryRef.current !== trimmed) return;
        setCandidates([]);
        setError("Lookup unavailable — try again in a moment.");
      } finally {
        if (latestQueryRef.current === trimmed) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const matchCountLabel = useMemo(() => {
    const len = query.trim().length;
    if (len === 0) {
      return "Type an address, BIN, or BBL to search NYC records";
    }
    if (len < MIN_QUERY) {
      // Typing started, lookup not yet fired: say WHY nothing is happening
      // rather than repeat the idle invitation. Without it a 1-2 char query
      // reads as a dead search box (Leo, 2026-07-23).
      return `Keep typing — search starts at ${MIN_QUERY} characters`;
    }
    if (loading) return "Searching NYC records…";
    if (error) return error;
    if (candidates.length === 0) return "No matches in NYC records";
    return `${candidates.length} ${
      candidates.length === 1 ? "match" : "matches"
    } — click to continue`;
  }, [query, loading, error, candidates]);

  return (
    // Per the design: the outer modal supplies a 14px border-radius, this
    // panel pads 28px L/R, 28px top, 24px bottom. Inter for body, Newsreader
    // for the headline via the `font-display` token.
    <div className="px-7 pt-7 pb-6">
      <MonoLabel>Step 1 of 2 · Locate</MonoLabel>
      <h2 className="font-display text-[28px] font-normal leading-tight tracking-[-0.6px] text-ink mt-2 mb-1.5">
        Add a building.
      </h2>
      <p className="text-sm leading-[1.55] text-ink-mute mb-[22px]">
        Paste an address, BIN, or BBL. We pull the rest from city
        records — no spreadsheet wrangling.
      </p>

      {/* Search field: 1px ink border, radius 10, height 48, ⌕ icon at
          start; active state mirrors the design's ink-on-ink chrome. */}
      <div className="flex items-center gap-2.5 h-12 px-3.5 mb-2 border border-ink rounded-[10px] focus-within:ring-2 focus-within:ring-teal/30">
        <Search
          className="h-4 w-4 text-ink-mute shrink-0"
          aria-hidden="true"
        />
        <input
          type="text"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="112 Madison Avenue · 1016941 · 1008590072"
          aria-label="Locate building"
          className="flex-1 bg-transparent text-[15px] text-ink placeholder:text-ink-mute focus:outline-none"
        />
        {loading ? (
          <Loader2
            className="h-3.5 w-3.5 text-ink-mute animate-spin shrink-0"
            aria-hidden="true"
          />
        ) : null}
      </div>

      {/* role=status so the "keep typing / N matches / no matches"
          transitions are announced as the user types; the visual label
          alone is invisible to screen readers. */}
      <div role="status" aria-live="polite">
        <MonoLabel className="mb-3.5">{matchCountLabel}</MonoLabel>
      </div>

      {/* Match list — only rendered when there's something to show. */}
      {candidates.length > 0 ? (
        <div className="rounded-[10px] border border-ink-line bg-paper-panel overflow-hidden">
          {candidates.map((c, i) => {
            const isExisting = !!(
              c.bin && existingBins && existingBins.has(c.bin)
            );
            return (
              <button
                key={c.bin ?? `${c.bbl}-${i}`}
                type="button"
                disabled={isExisting}
                onClick={() => onPick(c)}
                data-testid={`locate-match-${c.bin ?? i}`}
                className={
                  "group w-full text-left px-4 py-3 flex items-center justify-between gap-3 " +
                  (i > 0 ? "border-t border-ink-line " : "") +
                  (isExisting
                    ? "opacity-60 cursor-not-allowed"
                    : "bg-transparent hover:bg-paper motion-tint cursor-pointer")
                }
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-ink truncate">
                    {c.addressResolved ?? "Unknown address"}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.3px] text-ink-mute mt-0.5">
                    {c.bin ? `BIN ${c.bin}` : null}
                    {c.bin && c.bbl ? " · " : null}
                    {c.bbl ? `BBL ${c.bbl}` : null}
                    {isExisting ? " · already in your buildings" : null}
                  </div>
                </div>
                {!isExisting ? (
                  <span
                    className="text-base text-teal shrink-0"
                    aria-hidden="true"
                  >
                    →
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Footer: the CSV bulk-upload affordance as an inline link, per
          A1_Entry. 12px ink-mute, top-bordered with a hairline. */}
      <div className="mt-[18px] pt-4 border-t border-ink-line flex items-center justify-between text-xs text-ink-mute">
        <span>
          Outside NYC?{" "}
          <button
            type="button"
            className="text-ink underline underline-offset-2 hover:no-underline"
            onClick={() => {
              /* TODO(redesign): manual entry ships later — the Step-2
                 confirm panel will accept a fully typed building when no
                 NYC record matches. */
            }}
          >
            Enter manually →
          </button>
        </span>
        {onOpenCsv ? (
          <button
            type="button"
            onClick={onOpenCsv}
            className="hover:text-ink"
          >
            Bulk upload (CSV)
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default AddBuildingLocatePanel;
